import { z } from 'zod';
import { PositionKindSchema } from './org';

export const PersonKindSchema = z.enum(['staff', 'external']);
export const PersonStatusSchema = z.enum(['active', 'left', 'blocked']);
export const EmploymentStatusSchema = z.enum(['employed', 'left', 'temporary']);
export const AccountStatusSchema = z.enum(['active', 'locked', 'disabled']);

export const IdentitySystemSchema = z.enum([
  'autopart_inits',
  'quartix_driver',
  'pbx_extension',
  'rfid_fob',
  'legacy_user_id',
  'legacy_staff_id',
  'legacy_username',
]);

/** Account state as the People list shows it (never the hash or lock details). */
export const LoginStateSchema = z.enum(['none', 'active', 'locked', 'disabled']);

/** One row of the People list. */
export const PersonListItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  initials: z.string(),
  kind: PersonKindSchema,
  status: PersonStatusSchema,
  staffNumber: z.string().nullable(),
  jobTitle: z.string().nullable(),
  /** Home site (from the staff record); external people have none. */
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  /** Current seats, e.g. "Branch Manager · Stafford". */
  positions: z.array(
    z.object({ title: z.string(), kind: PositionKindSchema, scopeName: z.string() }),
  ),
  login: LoginStateSchema,
});

export const PeopleListQuerySchema = z.object({
  /** Matches name, staff number, login email or Autopart initials. */
  q: z.string().trim().max(100).optional(),
  /** Site code (home site or a current position at that site). */
  site: z.string().trim().max(32).optional(),
  /** Role code: held through a current position or an active grant. */
  role: z.string().trim().max(64).optional(),
  status: PersonStatusSchema.optional(),
  /** Opaque keyset cursor from the previous page. */
  cursor: z.string().max(400).optional(),
});

export const PeopleListResponseSchema = z.object({
  items: z.array(PersonListItemSchema),
  /** Present when there is another page. */
  nextCursor: z.string().nullable(),
  pageSize: z.number().int().positive(),
  /** Everyone matching the filters (first page only; later pages leave it out). */
  total: z.number().int().nonnegative().nullable().optional(),
});

export const PositionHistoryItemSchema = z.object({
  assignmentId: z.uuid(),
  positionId: z.uuid(),
  positionCode: z.string(),
  title: z.string(),
  kind: PositionKindSchema,
  /** The site, region or division the seat covers; company for a company-wide seat (D61). */
  scopeKind: z.enum(['site', 'region', 'division', 'company']),
  scopeCode: z.string(),
  scopeName: z.string(),
  startedOn: z.iso.date(),
  endedOn: z.iso.date().nullable(),
  isActing: z.boolean(),
});

export const IdentitySchema = z.object({
  id: z.uuid(),
  system: IdentitySystemSchema,
  value: z.string(),
  site: z.object({ code: z.string(), name: z.string() }).nullable(),
  startedOn: z.iso.date().nullable(),
  endedOn: z.iso.date().nullable(),
});

/** Employment basics only: nothing HR-sensitive (no DOB, pay, bank, medical). */
export const StaffOverviewSchema = z.object({
  staffNumber: z.string().nullable(),
  employmentStatus: EmploymentStatusSchema,
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable(),
  jobTitle: z.string().nullable(),
  contractHours: z.number().nullable(),
  homeSite: z.object({ code: z.string(), name: z.string() }).nullable(),
  lineManager: z
    .object({
      positionId: z.uuid(),
      positionTitle: z.string(),
      scopeName: z.string(),
      holderName: z.string().nullable(),
    })
    .nullable(),
});

export const AccountOverviewSchema = z.object({
  loginEmail: z.string().nullable(),
  status: AccountStatusSchema,
  lastLoginAt: z.iso.datetime().nullable(),
  mustChangePassword: z.boolean(),
});

export const PersonDetailSchema = z.object({
  id: z.uuid(),
  givenName: z.string(),
  familyName: z.string(),
  preferredName: z.string().nullable(),
  name: z.string(),
  initials: z.string(),
  kind: PersonKindSchema,
  organisation: z.string().nullable(),
  status: PersonStatusSchema,
  createdAt: z.iso.datetime(),
  staff: StaffOverviewSchema.nullable(),
  positions: z.array(PositionHistoryItemSchema),
  identities: z.array(IdentitySchema),
  /** null = no login. */
  account: AccountOverviewSchema.nullable(),
});

export type PersonKind = z.infer<typeof PersonKindSchema>;
export type PersonStatus = z.infer<typeof PersonStatusSchema>;
export type EmploymentStatus = z.infer<typeof EmploymentStatusSchema>;
export type AccountStatus = z.infer<typeof AccountStatusSchema>;
export type IdentitySystem = z.infer<typeof IdentitySystemSchema>;
export type LoginState = z.infer<typeof LoginStateSchema>;
export type PersonListItem = z.infer<typeof PersonListItemSchema>;
export type PeopleListQuery = z.infer<typeof PeopleListQuerySchema>;
export type PeopleListResponse = z.infer<typeof PeopleListResponseSchema>;
export type PositionHistoryItem = z.infer<typeof PositionHistoryItemSchema>;
export type Identity = z.infer<typeof IdentitySchema>;
export type StaffOverview = z.infer<typeof StaffOverviewSchema>;
export type AccountOverview = z.infer<typeof AccountOverviewSchema>;
export type PersonDetail = z.infer<typeof PersonDetailSchema>;
